import { LocalStorageKeys } from "@/components/pages/login/hooks/useLocalStorage";
import { toast } from "sonner";
import { LoginResponse } from "@/services/authService";
import { navigationService } from "@/utils/navigationService";
import { cacheService } from "@/utils/cacheService";
import { getSelectedHotelId } from "@/utils/hotelContext";
import {
  getUserFriendlyErrorMessage,
  isNetworkError,
} from "@/utils/httpErrors";

let isRefreshing = false;
let refreshSubscribers: Array<(token: string | null) => void> = [];

function onRefreshed(token: string | null) {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
}

function addRefreshSubscriber(callback: (token: string | null) => void) {
  refreshSubscribers.push(callback);
}

export interface RequestOptions extends RequestInit {
  disableToast?: boolean;
  _retryCount?: number;
  responseType?: "json" | "text" | "blob";
  skipSelectedHotelHeader?: boolean;
}

export class ApiError extends Error {
  statusCode: number;
  timestamp?: string;
  path?: string;
  details?: Record<string, unknown>;

  constructor(
    message: string,
    statusCode: number,
    timestamp?: string,
    path?: string,
    details?: Record<string, unknown>,
  ) {
    super(message);
    this.statusCode = statusCode;
    this.timestamp = timestamp;
    this.path = path;
    this.details = details;
    this.name = "ApiError";
  }
}

export class BaseService {
  protected baseUrl: string;
  protected defaultHeaders: Record<string, string>;

  constructor(baseUrl?: string) {
    this.baseUrl =
      baseUrl || process.env.REACT_APP_API_URL || "http://localhost:3001/api";
    this.defaultHeaders = {
      "Content-Type": "application/json",
    };
  }

  setAuthToken(token: string): void {
    this.defaultHeaders["Authorization"] = `Bearer ${token}`;
  }

  clearAuthToken(): void {
    delete this.defaultHeaders["Authorization"];
  }

  setHeaders(headers: Record<string, string>): void {
    this.defaultHeaders = { ...this.defaultHeaders, ...headers };
  }

  protected getAuthorizationHeader(): Record<string, string> | undefined {
    const token = localStorage.getItem(LocalStorageKeys.ACCESS_TOKEN);
    return token ? { Authorization: `Bearer ${token}` } : undefined;
  }

  protected getSelectedHotelHeader(): Record<string, string> | undefined {
    const hotelId = getSelectedHotelId();
    return hotelId ? { "x-hotel-id": hotelId } : undefined;
  }

  private showToast(
    message: string,
    type: "error" | "success",
    disableToast: boolean = false,
  ) {
    if (disableToast) return;

    if (type === "error") {
      toast.error(message);
    } else {
      toast.success(message);
    }
  }

  private async refreshTokenProcess(): Promise<string | null> {
    const refreshToken = localStorage.getItem(LocalStorageKeys.REFRESH_TOKEN);
    const tenantId = localStorage.getItem(LocalStorageKeys.TENANT_ID);

    if (!refreshToken || !tenantId) {
      return null;
    }

    try {
      const response = await fetch(`${this.baseUrl}/auth/refresh`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refreshToken, tenantId }),
      });

      if (!response.ok) {
        throw new Error(`Refresh failed: ${response.status}`);
      }

      const data = (await response.json()) as LoginResponse;

      if (data && data.refreshToken && data.accessToken) {
        localStorage.setItem(LocalStorageKeys.ACCESS_TOKEN, data.accessToken);
        localStorage.setItem(LocalStorageKeys.REFRESH_TOKEN, data.refreshToken);
        localStorage.setItem(LocalStorageKeys.TENANT_ID, data.user.tenantId);

        this.setAuthToken(data.accessToken);

        return data.accessToken;
      } else {
        return null;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  private clearTokensAndRedirect(disableToast: boolean = false): void {
    localStorage.removeItem(LocalStorageKeys.ACCESS_TOKEN);
    localStorage.removeItem(LocalStorageKeys.REFRESH_TOKEN);
    localStorage.removeItem(LocalStorageKeys.TENANT_ID);
    localStorage.removeItem(LocalStorageKeys.HOTEL_ID);

    this.clearAuthToken();

    refreshSubscribers = [];
    cacheService.clearAuthCache();

    if (!window.location.href.includes("/login")) {
      this.showToast("Unauthorized", "error", disableToast);
      navigationService.navigate("/login", { replace: true });
    }
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  protected async request<T>(
    endpoint: string,
    options: RequestOptions = {},
  ): Promise<T> {
    const {
      disableToast,
      _retryCount = 0,
      responseType,
      skipSelectedHotelHeader = false,
      ...fetchOptions
    } = options;

    const url = `${this.baseUrl}${endpoint}`;

    const headers: Record<string, string> = {
      ...this.defaultHeaders,
      ...this.getAuthorizationHeader(),
      ...(skipSelectedHotelHeader ? undefined : this.getSelectedHotelHeader()),
      // Explicit per-call headers win over the auto-injected auth/hotel headers
      // above — e.g. hotelService.getUserHotel() deliberately pins x-hotel-id to
      // the query's captured hotel id, which must not be silently overwritten by
      // whatever is currently in localStorage.
      ...(fetchOptions.headers as Record<string, string>),
    };

    if (fetchOptions.body instanceof FormData) {
      delete headers["Content-Type"];
    }

    const config: RequestInit = {
      ...fetchOptions,
      headers,
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        await this.handleErrorResponse(response);
      }

      if (
        response.status === 204 ||
        response.headers.get("content-length") === "0"
      ) {
        return undefined as T;
      }

      if (responseType === "blob") {
        return (await response.blob()) as unknown as T;
      }

      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        return await response.json();
      }

      return (await response.text()) as T;
    } catch (error) {
      const isUnauthorizedError = this.isUnauthorizedError(error);

      if (isUnauthorizedError && _retryCount < 3) {
        if (isRefreshing) {
          return new Promise<T>((resolve, reject) => {
            addRefreshSubscriber(async (newToken) => {
              if (newToken) {
                try {
                  const result = await this.request<T>(endpoint, {
                    ...options,
                    _retryCount: _retryCount + 1,
                  });
                  resolve(result);
                } catch (retryError) {
                  reject(retryError);
                }
              } else {
                reject(new ApiError("Authentication failed", 401));
              }
            });
          });
        }

        isRefreshing = true;

        try {
          const newToken = await this.refreshTokenProcess();

          if (newToken) {
            onRefreshed(newToken);
            isRefreshing = false;

            return await this.request<T>(endpoint, {
              ...options,
              _retryCount: _retryCount + 1,
            });
          } else {
            onRefreshed(null);
            isRefreshing = false;

            this.clearTokensAndRedirect(disableToast);
            throw new ApiError("Authentication failed", 401);
          }
        } catch (refreshError) {
          onRefreshed(null);
          isRefreshing = false;

          this.clearTokensAndRedirect(disableToast);
          throw refreshError instanceof ApiError
            ? refreshError
            : new ApiError("Authentication failed", 401);
        }
      }

      if (error instanceof ApiError) {
        if (!isUnauthorizedError) {
          this.showToast(
            getUserFriendlyErrorMessage(error),
            "error",
            disableToast,
          );
        }
        throw error;
      } else if (error instanceof Error) {
        const message = getUserFriendlyErrorMessage(error);
        this.showToast(message, "error", disableToast);
        throw this.createApiError(message, isNetworkError(error) ? 0 : 500);
      }
      throw error;
    }
  }

  private async handleErrorResponse(response: Response): Promise<never> {
    let errorMessage = `HTTP error! status: ${response.status}`;
    let errorCode = response.status;
    let errorDetails: Record<string, unknown> | undefined;

    try {
      const errorData = await response.json();
      const rawMessage = errorData.message || errorData.error;
      if (Array.isArray(rawMessage)) {
        errorMessage = rawMessage.join("\n");
      } else if (typeof rawMessage === "string" && rawMessage.trim()) {
        errorMessage = rawMessage;
      }
      errorCode = errorData.statusCode || errorData.code || errorCode;
      if (errorData && typeof errorData === "object") {
        errorDetails = errorData as Record<string, unknown>;
      }
    } catch {
      errorMessage = response.statusText || errorMessage;
    }

    throw this.createApiError(errorMessage, errorCode, errorDetails);
  }

  private createApiError(
    message: string,
    statusCode: number,
    details?: Record<string, unknown>,
  ): ApiError {
    return new ApiError(message, statusCode, undefined, undefined, details);
  }

  protected async get<T>(
    endpoint: string,
    params?: Record<string, any>,
    options?: Omit<RequestOptions, "method" | "body">,
  ): Promise<T> {
    const queryParams = params ? this.buildQueryString(params) : "";
    const url = `${endpoint}${queryParams ? "?" + queryParams : ""}`;
    return this.request<T>(url, { method: "GET", ...options });
  }

  protected async getBlob(
    endpoint: string,
    params?: Record<string, any>,
  ): Promise<Blob> {
    const queryParams = params ? this.buildQueryString(params) : "";
    const url = `${endpoint}${queryParams ? "?" + queryParams : ""}`;
    return this.request<Blob>(url, {
      method: "GET",
      responseType: "blob",
    });
  }

  protected async post<T>(
    endpoint: string,
    data?: any,
    options?: Omit<RequestOptions, "method" | "body">,
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });
  }

  protected async put<T>(
    endpoint: string,
    data?: any,
    options?: Omit<RequestOptions, "method" | "body">,
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: "PUT",
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });
  }

  protected async patch<T>(
    endpoint: string,
    data?: any,
    options?: Omit<RequestOptions, "method" | "body">,
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: "PATCH",
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });
  }

  protected async delete<T>(
    endpoint: string,
    data?: any,
    options?: Omit<RequestOptions, "method" | "body">,
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: "DELETE",
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });
  }

  private buildQueryString(params: Record<string, any>): string {
    const queryParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach((item) => queryParams.append(key, item.toString()));
        } else {
          queryParams.append(key, value.toString());
        }
      }
    });

    return queryParams.toString();
  }

  private isUnauthorizedError = (error: unknown): boolean => {
    return this.hasErrorStatus(error, 401);
  };

  private hasErrorStatus = (error: any, statusCode: number): boolean => {
    if (!error || typeof error !== "object") return false;

    return error.statusCode === statusCode || error.status === statusCode;
  };

  protected async uploadFile<T>(
    endpoint: string,
    file: File,
    additionalData?: Record<string, any>,
  ): Promise<T> {
    const formData = new FormData();
    formData.append("file", file);

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, value.toString());
      });
    }

    const headers = { ...this.defaultHeaders };
    delete headers["Content-Type"];

    return this.request<T>(endpoint, {
      method: "POST",
      headers,
      body: formData,
    });
  }
}
